/**
 * Combat Transition Service - Handles transitions between graphical mode and combat
 * Saves graphical state, manages transitions, and restores state after combat
 */

import { modeStateSyncService } from './modeStateSyncService';
import type { GraphicalPlayerState } from './modeStateSyncService';

export interface CombatTransitionState {
  graphicalState: GraphicalPlayerState;
  playerPosition: { x: number; y: number };
  location: string;
  timestamp: number;
  gameState?: any; // Full Zustand game state snapshot
}

class CombatTransitionService {
  private transitionState: CombatTransitionState | null = null;
  private readonly STORAGE_KEY = 'lucid_cyber_rpg_combat_transition_state';
  private readonly SESSION_BACKUP_KEY = 'lucid_cyber_graphical_state_backup';

  /**
   * Save graphical mode state before entering combat
   * Enhanced with sessionStorage backup and Zustand state snapshot
   */
  saveBeforeCombat(graphicalState: GraphicalPlayerState, playerPosition: { x: number; y: number }): void {
    // Import Zustand store dynamically to avoid circular dependencies
    import('@/state/gameStore').then(({ useGameStore }) => {
      const gameStateSnapshot = useGameStore.getState();

      this.transitionState = {
        graphicalState,
        playerPosition,
        location: graphicalState.location,
        timestamp: Date.now(),
        gameState: {
          currentScreen: gameStateSnapshot.currentScreen,
          userProfile: gameStateSnapshot.userProfile,
          currentPlayerState: gameStateSnapshot.currentPlayerState,
          currentStorySegment: gameStateSnapshot.currentStorySegment,
          selectedCartridge: gameStateSnapshot.selectedCartridge,
          currentRoom: gameStateSnapshot.currentRoom,
          isMultiplayer: gameStateSnapshot.isMultiplayer,
          chatMessages: gameStateSnapshot.chatMessages,
        },
      };

      // Save to both localStorage AND sessionStorage for redundancy
      try {
        const stateJson = JSON.stringify(this.transitionState);

        localStorage.setItem(this.STORAGE_KEY, stateJson);
        sessionStorage.setItem(this.SESSION_BACKUP_KEY, stateJson);

        console.log('[CombatTransition] ✅ Saved complete state before combat:', {
          location: this.transitionState.location,
          position: this.transitionState.playerPosition,
          hasGameState: !!this.transitionState.gameState,
          storage: 'localStorage + sessionStorage',
        });
      } catch (error) {
        console.error('[CombatTransition] ❌ Failed to save state:', error);
      }
    }).catch(error => {
      console.error('[CombatTransition] Failed to import gameStore:', error);
    });
  }

  /**
   * Get saved transition state
   * Enhanced to try sessionStorage first (more reliable for current session)
   */
  getTransitionState(): CombatTransitionState | null {
    // Try memory first
    if (this.transitionState) {
      console.log('[CombatTransition] Found state in memory');
      return this.transitionState;
    }

    // Try sessionStorage first (more reliable, won't persist across browser restarts)
    try {
      const sessionSaved = sessionStorage.getItem(this.SESSION_BACKUP_KEY);
      if (sessionSaved) {
        this.transitionState = JSON.parse(sessionSaved);
        console.log('[CombatTransition] ✅ Restored state from sessionStorage');
        return this.transitionState;
      }
    } catch (error) {
      console.error('[CombatTransition] Failed to read from sessionStorage:', error);
    }

    // Fallback to localStorage
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        this.transitionState = JSON.parse(saved);
        console.log('[CombatTransition] ✅ Restored state from localStorage');
        return this.transitionState;
      }
    } catch (error) {
      console.error('[CombatTransition] Failed to read from localStorage:', error);
    }

    console.warn('[CombatTransition] ⚠️ No saved state found in memory, sessionStorage, or localStorage');
    return null;
  }

  /**
   * Restore graphical mode state after combat ends
   * Enhanced to restore full Zustand game state
   */
  restoreAfterCombat(): GraphicalPlayerState | null {
    const state = this.getTransitionState();
    if (!state) {
      console.error('[CombatTransition] ❌ No transition state found, cannot restore');
      return null;
    }

    // CRITICAL: Restore Zustand game state BEFORE syncing to graphical mode
    if (state.gameState) {
      import('@/state/gameStore').then(({ useGameStore }) => {
        console.log('[CombatTransition] Restoring Zustand game state...');

        // Restore the game state snapshot
        useGameStore.setState({
          ...state.gameState,
          // Ensure we keep the current screen we're navigating to
          currentScreen: state.gameState.currentScreen || 'graphical',
        });

        console.log('[CombatTransition] ✅ Zustand state restored');
      }).catch(error => {
        console.error('[CombatTransition] Failed to restore Zustand state:', error);
      });
    } else {
      console.warn('[CombatTransition] ⚠️ No gameState snapshot found in transition state');
    }

    // Sync state back to graphical mode
    modeStateSyncService.syncFromGraphical(state.graphicalState);

    // Clear transition state from storage
    this.transitionState = null;
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      sessionStorage.removeItem(this.SESSION_BACKUP_KEY);
      console.log('[CombatTransition] Cleared transition state from storage');
    } catch (error) {
      console.error('[CombatTransition] Failed to clear storage:', error);
    }

    console.log('[CombatTransition] ✅ Restored graphical state after combat:', {
      location: state.location,
      position: state.playerPosition,
      hasGameState: !!state.gameState,
    });

    return state.graphicalState;
  }

  /**
   * Check if there's a pending transition to restore
   */
  hasPendingTransition(): boolean {
    return this.getTransitionState() !== null;
  }

  /**
   * Clear transition state (e.g., if user chooses not to return to graphical mode)
   */
  clearTransitionState(): void {
    this.transitionState = null;
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      sessionStorage.removeItem(this.SESSION_BACKUP_KEY);
      console.log('[CombatTransition] Cleared transition state from all storage');
    } catch (error) {
      console.error('[CombatTransition] Failed to clear storage:', error);
    }
  }
}

export const combatTransitionService = new CombatTransitionService();

